/**
 * Hook for fetching and submitting account data.
 */

import {
    fetchAccountData,
    updateAccountData
} from '@shared/services/account-service';
import { useCallback, useEffect, useState } from 'react';
import type { AccountResult } from '../types/account-types';

export interface UseAccountDataReturn {
    data: AccountResult | null;
    loading: boolean;
    error: string | null;
    saving: boolean;
    saveError: string | null;
    saveSuccess: boolean;
    reload: () => Promise<void>;
    save: (fields: Record<string, unknown>) => Promise<boolean>;
}

export function useAccountData(): UseAccountDataReturn {
    const [data, setData] = useState<AccountResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        const result = await fetchAccountData();
        if (result.success) {
            setData(result.data.result);
        } else {
            setError(result.error.message);
        }
        setLoading(false);
    }, []);

    const save = useCallback(async (fields: Record<string, unknown>): Promise<boolean> => {
        setSaving(true);
        setSaveError(null);
        setSaveSuccess(false);
        const result = await updateAccountData(fields);
        if (result.success) {
            setData(result.data.result);
            setSaveSuccess(true);
            setSaving(false);
            return true;
        } else {
            setSaveError(result.error.message);
            setSaving(false);
            return false;
        }
    }, []);

    useEffect(() => {
        reload();
    }, [reload]);

    return { data, loading, error, saving, saveError, saveSuccess, reload, save };
}
